import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { useCompany } from "../hooks/useCompany";
import DemoBanner from "./DemoBanner";
import { BILLING_URL } from "../pages/ManageSubscription";
import { IS_DEV } from "../stage";

function parseDate(raw) {
  if (!raw || raw === "N/A") return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

const navGroups = [
  { type: "link", path: "/", labelKey: "nav_dashboard", icon: "🏠" },
  {
    type: "group", labelKey: "nav_payroll", icon: "📅",
    items: [
      { path: "/ManageEmployees", labelKey: "nav_manage_employees", icon: "🏢" },
      { path: "/ManageWeeklyPayroll", labelKey: "nav_weekly_payroll", icon: "📅" },
      { path: "/PayrollHistory", labelKey: "nav_payroll_history", icon: "🔎" },
    ],
  },
  {
    type: "group", labelKey: "nav_checks", icon: "💸",
    items: [
      { path: "/SingleCheck", labelKey: "nav_print_single_check", icon: "💸" },
      { path: "/PrintWeeklyPayroll", labelKey: "nav_print_weekly_checks", icon: "🖨️" },
      { path: "/CheckHistory", labelKey: "nav_check_history", icon: "📋" },
    ],
  },
  {
    type: "group", labelKey: "nav_invoices_estimates", icon: "🧾",
    items: [
      { path: "/Invoices", labelKey: "nav_all_invoices", icon: "📄" },
      { path: "/CreateInvoice", labelKey: "nav_create_invoice", icon: "✏️" },
      { path: "/Estimates", labelKey: "nav_all_estimates", icon: "📋" },
      { path: "/CreateEstimate", labelKey: "nav_create_estimate", icon: "📝" },
    ],
  },
  {
    type: "group", labelKey: "nav_reports", icon: "📊",
    items: [
      { path: "/Generate1099", labelKey: "nav_generate_1099", icon: "📋" },
      { path: "/Filings1099",  labelKey: "nav_filings_1099",  icon: "🗂️" },
      { path: "/Generate1096", labelKey: "nav_generate_1096", icon: "📨" },
      { path: "/Filings1096",  labelKey: "nav_filings_1096",  icon: "🗃️" },
    ],
  },
  {
    type: "group", labelKey: "nav_bank_accounts", icon: "🏦", adminOnly: true,
    items: [
      { path: "/BankAccount", labelKey: "nav_connect_bank", icon: "🔗" },
      { path: "/CategorizeTransactions", labelKey: "nav_categorize_transactions", icon: "🏷️" },
      { path: "/GenerateAccountabilityStatement", labelKey: "nav_accountability_statement", icon: "📑" },
    ],
  },
  {
    type: "group", labelKey: "nav_settings", icon: "⚙️",
    items: [
      { path: "/settings", labelKey: "nav_user_settings", icon: "🔒" },
      { path: "/CompanySettings", labelKey: "nav_company_settings", icon: "🏢" },
      { path: "/ManageSubscription", labelKey: "nav_manage_subscription", icon: "💳" },
    ],
  },
];

// In a company someone shared (backend/acting.py), "use" sees day-to-day work only, and neither
// level sees the owner's billing, banks or company list. The API enforces the same.
const USE_PATHS = new Set(["/", "/ManageWeeklyPayroll", "/Invoices", "/CreateInvoice", "/Estimates", "/CreateEstimate", "/settings"]);
const OWNER_PATHS = new Set(["/BankAccount", "/CategorizeTransactions", "/GenerateAccountabilityStatement", "/CompanySettings", "/ManageSubscription"]);

function visibleTo(level) {
  const ok = (path) => level === "owner" || (!OWNER_PATHS.has(path) && (level === "admin" || USE_PATHS.has(path)));
  return navGroups
    .map((g) => (g.type === "group" ? { ...g, items: g.items.filter((i) => ok(i.path)) } : g))
    .filter((g) => (g.type === "group" ? g.items.length > 0 : ok(g.path)));
}

const Sidebar = ({ handleSignOut, disableNavigation = false, children }) => {
  const location = useLocation();
  const { companies, activeCompany, switchCompany, subscriptionData, userGroups, accessLevel, leaveSupport } = useCompany();
  const endSupport = () => leaveSupport(activeCompany?.shared?.support?.id);
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Load collapsed state from localStorage on mount
  useEffect(() => {
    const savedState = localStorage.getItem("sidebarCollapsed");
    if (savedState !== null) {
      setIsCollapsed(savedState === "true");
    }
  }, []);

  // Auto-open the group that contains the active route
  useEffect(() => {
    const active = {};
    navGroups.forEach((g) => {
      if (g.type === "group" && g.items.some((i) => i.path === location.pathname)) {
        active[g.labelKey] = true;
      }
    });
    setOpenGroups((prev) => ({ ...prev, ...active }));
  }, [location.pathname]);

  const toggleSidebar = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem("sidebarCollapsed", newState.toString());
  };

  const toggleGroup = (labelKey) => {
    if (disableNavigation) return;
    if (isCollapsed) {
      // Expand the sidebar and open this group in one click
      setIsCollapsed(false);
      localStorage.setItem("sidebarCollapsed", "false");
      setOpenGroups((prev) => ({ ...prev, [labelKey]: true }));
    } else {
      setOpenGroups((prev) => ({ ...prev, [labelKey]: !prev[labelKey] }));
    }
  };

  const isActive = (path) => location.pathname === path;

  const isAdmin =
    subscriptionData?.is_admin === true ||
    subscriptionData?.subscriptionStatus === "admin_bypass";
  const isSubscribed = ["active", "trialing", "admin_bypass"].includes(
    subscriptionData?.subscriptionStatus?.toLowerCase()
  );
  const isBeta = userGroups.includes("beta");

  const linkClass = (path) =>
    `flex items-center px-3 py-2 rounded-lg transition text-sm font-medium ${
      isActive(path)
        ? "bg-green-500 text-white"
        : disableNavigation
        ? "text-gray-400 cursor-not-allowed"
        : "text-gray-700 hover:bg-gray-100"
    } ${isCollapsed ? "justify-center" : ""}`;

  const renderSubscriptionBanner = () => {
    const support = activeCompany?.shared?.support;
    if (support) {
      // Staff support session (acting.py): always shown, even with the menu collapsed.
      const ends = support.endsAt
        ? new Date(support.endsAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "";
      return (
        <div className="bg-amber-50 border-l-4 border-amber-500 p-3 mb-4 rounded-r-lg" role="status">
          {!isCollapsed && (
            <>
              <p className="font-semibold text-amber-900 text-xs">{t("support_title")} {support.company}</p>
              <p className="text-xs text-amber-800">
                {t(support.level === "fix" ? "support_fix" : "support_view")}{ends ? ` · ${t("support_ends", { time: ends })}` : ""}
              </p>
            </>
          )}
          <button onClick={endSupport} className="mt-1 text-xs underline text-amber-900">{t("support_end")}</button>
        </div>
      );
    }

    if (isCollapsed) return null;

    if (activeCompany?.shared) {
      return (
        <div className="bg-gray-50 border-l-4 border-gray-300 p-3 mb-4 rounded-r-lg">
          <p className="font-semibold text-gray-800 text-xs">{t("team_shared_title")}</p>
          <p className="text-xs text-gray-600">
            {t(accessLevel === "admin" ? "team_shared_admin" : "team_shared_use")}
          </p>
        </div>
      );
    }

    if (isAdmin) {
      return (
        <div className="bg-purple-100 border-l-4 border-purple-500 p-3 mb-4 rounded-r-lg">
          <div className="flex items-center">
            <span className="text-2xl mr-2">👑</span>
            <div>
              <p className="font-semibold text-purple-900 text-sm">{t("sub_admin_title")}</p>
              <p className="text-xs text-purple-700">{t("sub_admin_body")}</p>
            </div>
          </div>
        </div>
      );
    }

    if (!subscriptionData) return null;

    const status = subscriptionData.subscriptionStatus?.toLowerCase();
    const endDate = parseDate(subscriptionData.subscriptionEnd);
    const daysUntilEnd = endDate
      ? Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24))
      : null;

    // Only in the last 7 days of a billing period (always on dev, to test it): whether it renews
    // and what it will be charged to, or that access ends. Nothing for a past date (a stale cache)
    // or while payments are paused in Stripe.
    const live = status === "active" || status === "trialing";
    const soon = daysUntilEnd !== null && daysUntilEnd >= 0 && (daysUntilEnd <= 7 || IS_DEV);
    if (live && soon && !subscriptionData.collectionPaused) {
      const date = endDate.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
      const when = daysUntilEnd === 0 ? t("sub_when_today") : t("sub_when_days", { count: daysUntilEnd });
      const renews = !subscriptionData.cancelAtPeriodEnd;
      const amount = typeof subscriptionData.billingAmount === "number"
        ? subscriptionData.billingAmount.toLocaleString(undefined, { style: "currency", currency: "USD" })
        : null;
      const body = !renews
        ? t("sub_ends_body", { date })
        : amount && subscriptionData.paymentMethod
          ? t("sub_renews_body_card", { amount, card: subscriptionData.paymentMethod, date })
          : t("sub_renews_body", { date });
      return (
        <div className={`${renews ? "bg-yellow-50 border-yellow-500" : "bg-orange-50 border-orange-500"} border-l-4 p-3 mb-4 rounded-r-lg`}>
          <p className={`font-semibold text-xs ${renews ? "text-yellow-900" : "text-orange-900"}`}>
            {t(renews ? "sub_renews_title" : "sub_ends_title", { when })}
          </p>
          <p className={`text-xs ${renews ? "text-yellow-700" : "text-orange-700"}`}>{body}</p>
          <a href={BILLING_URL} className={`text-xs underline ${renews ? "text-yellow-800" : "text-orange-800"}`}>
            {t(renews ? "sub_manage_link" : "sub_keep_link")}
          </a>
        </div>
      );
    }

    if (status === "past_due") {
      return (
        <div className="bg-red-50 border-l-4 border-red-500 p-3 mb-4 rounded-r-lg">
          <div className="flex items-center">
            <span className="text-xl mr-2">⚠️</span>
            <div>
              <p className="font-semibold text-red-900 text-xs">{t("sub_past_due_title")}</p>
              <p className="text-xs text-red-700">{t("sub_past_due_body")}</p>
              <a href={BILLING_URL} className="text-xs underline text-red-800">{t("sub_update_card_link")}</a>
            </div>
          </div>
        </div>
      );
    }

    if (!isSubscribed) {
      return (
        <div className="bg-blue-50 border-l-4 border-blue-500 p-3 mb-4 rounded-r-lg">
          <div className="flex items-center">
            <span className="text-xl mr-2">💡</span>
            <div>
              <p className="font-semibold text-blue-900 text-xs">{t("sub_free_title")}</p>
              <p className="text-xs text-blue-700">{t("sub_free_body")}</p>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderNavItem = (entry) => {
    if (entry.type === "link") {
      return (
        <Link
          key={entry.path}
          to={disableNavigation ? "#" : entry.path}
          onClick={disableNavigation ? (e) => e.preventDefault() : () => setIsMobileOpen(false)}
          className={linkClass(entry.path)}
          title={
            isCollapsed
              ? t(entry.labelKey)
              : disableNavigation
              ? t("nav_disabled")
              : t(entry.labelKey)
          }
        >
          <span className={`text-xl ${isCollapsed ? "" : "mr-3"}`}>{entry.icon}</span>
          {!isCollapsed && <span>{t(entry.labelKey)}</span>}
        </Link>
      );
    }

    // Group
    const isOpen = !!openGroups[entry.labelKey];
    const groupActive = entry.items.some((i) => isActive(i.path));

    return (
      <div key={entry.labelKey}>
        {/* Group header button */}
        <button
          onClick={() => toggleGroup(entry.labelKey)}
          className={`w-full flex items-center px-3 py-2 rounded-lg transition text-sm font-medium ${
            groupActive && !isOpen
              ? "bg-green-100 text-green-700"
              : disableNavigation
              ? "text-gray-400 cursor-not-allowed"
              : "text-gray-700 hover:bg-gray-100"
          } ${isCollapsed ? "justify-center" : ""}`}
          title={isCollapsed ? t(entry.labelKey) : undefined}
          disabled={disableNavigation}
        >
          <span className={`text-xl ${isCollapsed ? "" : "mr-3"}`}>{entry.icon}</span>
          {!isCollapsed && (
            <>
              <span className="flex-1 text-left">{t(entry.labelKey)}</span>
              <span className="text-xs text-gray-400 ml-1">
                {isOpen ? "▲" : "▼"}
              </span>
            </>
          )}
        </button>

        {/* Child items — hidden when collapsed or closed */}
        {!isCollapsed && isOpen && (
          <div className="ml-4 mt-1 flex flex-col space-y-1 border-l-2 border-gray-100 pl-2">
            {entry.items.map((item) => (
              <Link
                key={item.path}
                to={disableNavigation ? "#" : item.path}
                onClick={disableNavigation ? (e) => e.preventDefault() : () => setIsMobileOpen(false)}
                className={linkClass(item.path)}
                title={
                  disableNavigation ? t("nav_disabled") : t(item.labelKey)
                }
              >
                <span className="text-base mr-3">{item.icon}</span>
                <span>{t(item.labelKey)}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`no-print fixed left-0 top-0 h-screen bg-white shadow-lg transition-all duration-300 z-50 flex flex-col ${
          isCollapsed ? "w-16" : "w-64"
        } ${isMobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
      >
        {/* Toggle Button — desktop only */}
        <button
          onClick={toggleSidebar}
          className="absolute -right-3 top-6 bg-green-500 text-white rounded-full w-6 h-6 items-center justify-center hover:bg-green-600 transition shadow-lg z-10 hidden md:flex"
          title={isCollapsed ? t("expand_sidebar") : t("collapse_sidebar")}
        >
          {isCollapsed ? "›" : "‹"}
        </button>

        {/* Mobile close button */}
        <button
          onClick={() => setIsMobileOpen(false)}
          className="absolute right-2 top-2 md:hidden text-gray-400 hover:text-gray-600 p-1 rounded"
          aria-label="Close menu"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Logo/Brand */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-center">
          {isCollapsed ? (
            <img src="/assets/images/Payclear512.png" alt="PayClear" className="w-8 h-8 object-contain" />
          ) : (
            <img src="/assets/images/Payclear800.png" alt="PayClear" className="h-8 object-contain" />
          )}
        </div>

        {/* Subscription Banner */}
        <div className="px-2 pt-4">{renderSubscriptionBanner()}</div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-2 py-4">
          <div className="flex flex-col space-y-1">
            {visibleTo(accessLevel)
              .filter((entry) => !entry.adminOnly || isAdmin || (isBeta && isSubscribed))
              .map((entry) => renderNavItem(entry))}
          </div>
        </nav>

        {/* Company Switcher */}
        {!isCollapsed && companies.length > 0 && (
          <div className="px-2 pb-2 border-t border-gray-200 pt-2">
            <p className="text-xs text-gray-400 uppercase font-semibold px-1 mb-1">{t("sidebar_active_company")}</p>
            <select
              value={activeCompany?.company_id || ""}
              onChange={(e) => {
                const newId = e.target.value;
                const newName = companies.find((c) => c.company_id === newId)?.name || "";
                Swal.fire({
                  title: t("switch_company_title"),
                  text: t("switch_company_text", { name: newName }),
                  icon: "question",
                  showCancelButton: true,
                  confirmButtonText: t("switch_company_confirm"),
                  cancelButtonText: t("cancel"),
                  confirmButtonColor: "#22c55e",
                }).then((result) => {
                  if (result.isConfirmed) {
                    switchCompany(newId);
                  }
                });
              }}
              disabled={disableNavigation || companies.length <= 1}
              className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-gray-50 text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-400 disabled:opacity-60"
            >
              {companies.map((c) => (
                <option key={c.company_id} value={c.company_id}>
                  {c.name}{c.is_default ? " ★" : ""}{c.shared ? ` (${t("team_shared_tag")})` : ""}
                </option>
              ))}
            </select>
          </div>
        )}
        {isCollapsed && companies.length > 1 && (
          <div className="px-2 pb-2 border-t border-gray-200 pt-2 flex justify-center">
            <span title={activeCompany?.name || "Company"} className="text-xl">🏢</span>
          </div>
        )}

        {/* Sign Out Button */}
        <div className="p-2 border-t border-gray-200">
          <button
            onClick={disableNavigation ? undefined : handleSignOut}
            disabled={disableNavigation}
            className={`w-full flex items-center px-3 py-2 rounded-lg transition ${
              disableNavigation
                ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                : "bg-red-500 text-white hover:bg-red-600"
            } ${isCollapsed ? "justify-center" : ""}`}
            title={
              isCollapsed
                ? disableNavigation
                  ? t("sign_out_disabled")
                  : t("sign_out")
                : disableNavigation
                ? t("sign_out_disabled")
                : ""
            }
          >
            <span className={`text-xl ${isCollapsed ? "" : "mr-3"}`}>🚪</span>
            {!isCollapsed && <span className="text-sm font-medium">{t("sign_out")}</span>}
          </button>
        </div>
      </div>

      {/* Content area: full width on mobile, margin on desktop */}
      <div className={`sidebar-content-wrapper transition-all duration-300 flex-1 flex flex-col ${isCollapsed ? "md:ml-16" : "md:ml-64"}`}>
        {/* Mobile top bar with hamburger */}
        <div className="no-print md:hidden flex items-center bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-30">
          <button
            onClick={() => setIsMobileOpen(true)}
            className="text-gray-600 hover:text-gray-800 p-1 rounded"
            aria-label="Open menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <img src="/assets/images/Payclear800.png" alt="PayClear" className="ml-3 h-7 object-contain" />
        </div>
        <DemoBanner />
        {children}
      </div>

    </>
  );
};

export default Sidebar;
